import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'
import { supabase, type MealTemplate } from '../lib/supabase'
import { isPlusRequiredError } from '../lib/billing'
import { sameNamed, TEMPLATE_NAME_MAX, type TemplateMeal } from '../lib/mealTemplates'
import { revertRow, snapshotRow, type RowSnapshot } from '../lib/rows'
import { useIsVisible } from './useIsVisible'

const TEMPLATES_KEY = (householdId: string) => ['meal-templates', householdId]

const byName = (a: MealTemplate, b: MealTemplate) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })

/**
 * The household's week templates (036 meal_templates), by name. Saving under a
 * name the household already uses replaces that template — "Everyday week"
 * saved again is an update, not a second one. Optimistic, rolled back on
 * error (a 42501 also calls onPlusRequired); a partner's arrive over Realtime.
 */
export function useMealTemplates(householdId: string, memberId: string, options: { onPlusRequired?: () => void } = {}) {
  const qc = useQueryClient()
  const isVisible = useIsVisible()
  const key = TEMPLATES_KEY(householdId)
  const { onPlusRequired } = options

  const query = useQuery({
    queryKey: key,
    enabled: !!householdId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('meal_templates').select('*')
        .eq('household_id', householdId)
      if (error) throw error
      return (data as MealTemplate[]).sort(byName)
    },
  })

  useEffect(() => {
    if (!isVisible || !householdId) return
    const channel = supabase
      .channel(`meal_templates:${householdId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'meal_templates', filter: `household_id=eq.${householdId}` },
        (payload) => {
          qc.setQueryData<MealTemplate[]>(TEMPLATES_KEY(householdId), (old = []) => {
            if (payload.eventType === 'DELETE') return old.filter(t => t.id !== (payload.old as MealTemplate).id)
            const row = payload.new as MealTemplate
            return [...old.filter(t => t.id !== row.id), row].sort(byName)
          })
        })
      // Joined, or joined again after the tab was hidden: read what realtime missed.
      .subscribe(status => {
        if (status === 'SUBSCRIBED') void qc.invalidateQueries({ queryKey: TEMPLATES_KEY(householdId) })
      })
    return () => { supabase.removeChannel(channel) }
  }, [householdId, qc, isVisible])

  // A refused write (Plus needed) takes back its own template only; a
  // partner's meanwhile stays (lib/rows.ts).
  const rollback = {
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: key })
      return { id, snap: snapshotRow(qc.getQueryData<MealTemplate[]>(key) ?? [], id) }
    },
    onError: (err: unknown, _vars: unknown, ctx: { id: string; snap: RowSnapshot<MealTemplate> } | undefined) => {
      if (ctx) qc.setQueryData<MealTemplate[]>(key, old => revertRow(old ?? [], ctx.id, ctx.snap))
      if (isPlusRequiredError(err)) onPlusRequired?.()
    },
  }

  const upsert = useMutation({
    ...rollback,
    mutationFn: async ({ row, isNew }: { row: MealTemplate; isNew: boolean }) => {
      const { error } = isNew
        ? await supabase.from('meal_templates').insert({
          id: row.id, household_id: row.household_id, name: row.name, meals: row.meals, created_by: row.created_by,
        })
        : await supabase.from('meal_templates').update({ name: row.name, meals: row.meals }).eq('id', row.id)
      if (error) throw error
    },
    onMutate: async ({ row }) => {
      const ctx = await rollback.onMutate(row.id)
      qc.setQueryData<MealTemplate[]>(key, (old = []) => [...old.filter(t => t.id !== row.id), row].sort(byName))
      return ctx
    },
  })

  const remove = useMutation({
    ...rollback,
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('meal_templates').delete().eq('id', id)
      if (error) throw error
    },
    onMutate: async (id: string) => {
      const ctx = await rollback.onMutate(id)
      qc.setQueryData<MealTemplate[]>(key, (old = []) => old.filter(t => t.id !== id))
      return ctx
    },
  })

  const templates = query.data ?? []
  return {
    templates,
    isLoading: query.isLoading,
    /** Saves `meals` as `name`, replacing a template of the same name; returns its id. */
    saveTemplate: (name: string, meals: TemplateMeal[]): string => {
      const clean = name.trim().replace(/\s+/g, ' ').slice(0, TEMPLATE_NAME_MAX)
      const existing = sameNamed(templates, clean)
      const now = new Date().toISOString()
      const row: MealTemplate = existing
        ? { ...existing, name: clean, meals, updated_at: now }
        : { id: uuid(), household_id: householdId, name: clean, meals, created_by: memberId || null, created_at: now, updated_at: now }
      upsert.mutate({ row, isNew: !existing })
      return row.id
    },
    deleteTemplate: (id: string) => remove.mutate(id),
  }
}
